import { useCallback, useMemo } from "react";

import { useAuth } from "@/context/AuthContext";

/**
 * usePermissions
 * ============================================================================
 *
 * Fachada de autorização para componentes React.
 *
 * REGRA ARQUITETURAL:
 *   authorization.py = fonte de verdade no backend.
 *   AuthContext       = transporte/cache da autorização efetiva.
 *   usePermissions    = API ergonômica para a interface.
 *
 * Este hook NÃO cria uma matriz paralela de permissões.
 * Ele somente consulta o contexto recebido do backend.
 *
 * Permissão responde "o quê".
 * Escopo responde "onde".
 * Feature responde "qual capacidade está habilitada".
 * Configuração responde "qual grupo administrativo pode ser acessado".
 */

const EMPTY_OBJECT = Object.freeze({});
const EMPTY_ARRAY = Object.freeze([]);

const ADMIN_ROLES = new Set([
  "admin",
]);

const ROLE_ALIASES = Object.freeze({
  rt: "responsavel_tecnico",
  responsavel_tecnico: "responsavel_tecnico",
  tecnico: "tecnico",
  tecnico_de_campo: "tecnico",
  coordenador: "coordenador",
  supervisor: "supervisor",
  gestor: "gestor",
  cliente: "cliente",
  auditor: "auditor",
  admin: "admin",
});

const SCOPE_KEYS = Object.freeze([
  "company_ids",
  "client_ids",
  "unit_ids",
  "area_ids",
  "subarea_ids",
]);

const normalizeRole = (role) => {
  const normalized = String(role || "")
    .trim()
    .toLowerCase();

  return ROLE_ALIASES[normalized] || normalized;
};

const objectOrEmpty = (value) =>
  value &&
  typeof value === "object" &&
  !Array.isArray(value)
    ? value
    : EMPTY_OBJECT;

const arrayOrEmpty = (value) =>
  Array.isArray(value)
    ? value
    : EMPTY_ARRAY;

const boolValue = (value) =>
  value === true ||
  value === 1 ||
  value === "true" ||
  value === "1";

const normalizeId = (value) => {
  if (
    value === null ||
    value === undefined
  ) {
    return null;
  }

  const normalized = String(value).trim();

  return normalized || null;
};

const normalizeIds = (value) => {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map(normalizeId)
          .filter(Boolean),
      ),
    );
  }

  if (
    value !== null &&
    value !== undefined
  ) {
    const normalized = normalizeId(value);

    return normalized
      ? [normalized]
      : [];
  }

  return [];
};

const collectBooleanMap = (value) =>
  Object.fromEntries(
    Object.entries(
      objectOrEmpty(value),
    ).map(([key, enabled]) => [
      key,
      boolValue(enabled),
    ]),
  );

const readNested = (
  root,
  keys,
) => {
  let cursor = root;

  for (const key of keys) {
    if (
      !cursor ||
      typeof cursor !== "object"
    ) {
      return undefined;
    }

    cursor = cursor[key];
  }

  return cursor;
};

export function usePermissions() {
  const auth = useAuth();

  const {
    user,
    authorization,
    authorizationLoading = false,
    authorizationError = null,
    refreshAuthorization,
  } = auth;

  /*
   * A autorização entregue pelo backend é a única fonte de permissões,
   * módulos, features, configuração e escopo.
   *
   * `user` permanece disponível para identidade/compatibilidade, mas não
   * funciona como uma segunda matriz de autorização.
   */
  const effective = authorization || null;

  const role = useMemo(
    () =>
      normalizeRole(
        authorization?.role ??
          user?.role,
      ),
    [
      effective?.role,
      user?.role,
    ],
  );

  const isAuthenticated =
    Boolean(user);

  const isAdmin = ADMIN_ROLES.has(role);

  const isGlobal =
    isAdmin ||
    boolValue(
      authorization?.global,
    ) ||
    boolValue(
      authorization?.scope?.global,
    ) ||
    boolValue(
      authorization?.scope_matrix
        ?.global,
    );

  const matrix = useMemo(
    () =>
      objectOrEmpty(
        authorization?.permissions,
      ),
    [
      authorization?.permissions,
    ],
  );

  const permissionMatrix =
    useMemo(
      () =>
        objectOrEmpty(
          authorization?.permission_matrix ??
            matrix,
        ),
      [
        authorization?.permission_matrix,
        matrix,
      ],
    );

  const modules = useMemo(
    () =>
      objectOrEmpty(
        authorization?.modules,
      ),
    [
      authorization?.modules,
    ],
  );

  const modulePermissions =
    useMemo(
      () =>
        objectOrEmpty(
          authorization?.module_permissions,
        ),
      [
        authorization?.module_permissions,
      ],
    );

  const features = useMemo(
    () =>
      collectBooleanMap(
        authorization?.features,
      ),
    [
      authorization?.features,
    ],
  );

  const configAccess = useMemo(
    () =>
      collectBooleanMap(
        authorization?.config_access ??
          authorization?.config_groups,
      ),
    [
      authorization?.config_access,
      authorization?.config_groups,
    ],
  );

  const scope = useMemo(
    () => {
      const raw =
        authorization?.scope ??
        EMPTY_OBJECT;

      const result = {};

      for (const key of SCOPE_KEYS) {
        result[key] =
          normalizeIds(
            raw?.[key],
          );
      }

      return result;
    },
    [
      authorization?.scope,
    ],
  );

  const scopeMatrix = useMemo(
    () =>
      objectOrEmpty(
        authorization?.scope_matrix,
      ),
    [
      authorization?.scope_matrix,
    ],
  );

  const rolePolicy = useMemo(
    () =>
      objectOrEmpty(
        authorization?.role_policy,
      ),
    [
      authorization?.role_policy,
    ],
  );

  const delegation = useMemo(
    () =>
      objectOrEmpty(
        authorization?.delegation,
      ),
    [
      authorization?.delegation,
    ],
  );

  /*
   * =========================================================================
   * PERMISSÕES
   * =========================================================================
   */

  const can = useCallback(
    (module, action) => {
      if (!module || !action) {
        return false;
      }

      if (isAdmin) {
        return true;
      }

      const normalizedModule =
        String(module).trim();

      const normalizedAction =
        String(action).trim();

      const moduleAllowed =
        boolValue(
          modules?.[
            normalizedModule
          ],
        );

      if (!moduleAllowed) {
        return false;
      }

      return boolValue(
        matrix?.[
          normalizedModule
        ]?.[
          normalizedAction
        ],
      );
    },
    [
      isAdmin,
      matrix,
      modules,
    ],
  );

  const canAny = useCallback(
    (
      module,
      actions = [],
    ) => {
      if (!module) {
        return false;
      }

      const normalizedActions =
        Array.isArray(actions)
          ? actions
              .map(
                (action) =>
                  String(
                    action || "",
                  ).trim(),
              )
              .filter(Boolean)
          : [];

      if (
        !normalizedActions.length
      ) {
        return false;
      }

      return normalizedActions.some(
        (action) =>
          can(module, action),
      );
    },
    [can],
  );

  const canAll = useCallback(
    (
      module,
      actions = [],
    ) => {
      if (!module) {
        return false;
      }

      const normalizedActions =
        Array.isArray(actions)
          ? actions
              .map(
                (action) =>
                  String(
                    action || "",
                  ).trim(),
              )
              .filter(Boolean)
          : [];

      if (
        !normalizedActions.length
      ) {
        return false;
      }

      return normalizedActions.every(
        (action) =>
          can(module, action),
      );
    },
    [can],
  );

  const hasModule = useCallback(
    (module) => {
      if (!module) {
        return false;
      }

      if (isAdmin) {
        return true;
      }

      return boolValue(
        modules?.[
          String(module).trim()
        ],
      );
    },
    [
      isAdmin,
      modules,
    ],
  );

  /*
   * Alias utilizado pelo Layout e por consumidores legados.
   */
  const canModuleView =
    useCallback(
      (module) =>
        hasModule(module),
      [hasModule],
    );

  const hasAnyModule =
    useCallback(
      (moduleList = []) => {
        if (isAdmin) {
          return true;
        }

        return arrayOrEmpty(
          moduleList,
        ).some((module) =>
          hasModule(module),
        );
      },
      [
        hasModule,
        isAdmin,
      ],
    );

  const hasAllModules =
    useCallback(
      (moduleList = []) =>
        arrayOrEmpty(
          moduleList,
        ).every((module) =>
          hasModule(module),
        ),
      [hasModule],
    );

  const hasFeature =
    useCallback(
      (feature) => {
        if (!feature) {
          return false;
        }

        if (isAdmin) {
          return true;
        }

        return boolValue(
          features?.[
            String(feature).trim()
          ],
        );
      },
      [
        features,
        isAdmin,
      ],
    );

  const hasAnyFeature =
    useCallback(
      (featureList = []) => {
        if (isAdmin) {
          return true;
        }

        return arrayOrEmpty(
          featureList,
        ).some((feature) =>
          hasFeature(feature),
        );
      },
      [
        hasFeature,
        isAdmin,
      ],
    );

  const hasAllFeatures =
    useCallback(
      (featureList = []) =>
        arrayOrEmpty(
          featureList,
        ).every((feature) =>
          hasFeature(feature),
        ),
      [hasFeature],
    );

  /*
   * =========================================================================
   * CONFIGURAÇÕES
   * =========================================================================
   */

  const canConfig =
    useCallback(
      (group) => {
        if (!group) {
          return false;
        }

        if (isAdmin) {
          return true;
        }

        return boolValue(
          configAccess?.[
            String(group).trim()
          ],
        );
      },
      [
        configAccess,
        isAdmin,
      ],
    );

  const hasConfig =
    useCallback(
      (group) =>
        canConfig(group),
      [canConfig],
    );

  const canAnyConfig =
    useCallback(
      (groups = []) => {
        if (isAdmin) {
          return true;
        }

        return arrayOrEmpty(
          groups,
        ).some((group) =>
          canConfig(group),
        );
      },
      [
        canConfig,
        isAdmin,
      ],
    );

  /*
   * =========================================================================
   * ESCOPO
   * =========================================================================
   *
   * Fail closed para usuários comuns.
   *
   * Escopo controla somente o alcance dos DADOS OPERACIONAIS.
   * Ele não concede nem revoga ações administrativas.
   *
   * Hierarquia:
   * company → client → unit → area → subarea
   *
   * Quando um nível inferior é consultado, o pai restrito também precisa
   * ser informado, exatamente para evitar que a UI fabrique uma consulta
   * fora do escopo.
   */

  const inScope = useCallback(
    ({
      company_id: companyId,
      client_id: clientId,
      unit_id: unitId,
      area_id: areaId,
      subarea_id: subareaId,
      companyId: companyIdCamel,
      clientId: clientIdCamel,
      unitId: unitIdCamel,
      areaId: areaIdCamel,
      subareaId: subareaIdCamel,
    } = {}) => {
      if (isGlobal) {
        return true;
      }

      const requested = {
        company_id:
          normalizeId(
            companyId ??
              companyIdCamel,
          ),
        client_id:
          normalizeId(
            clientId ??
              clientIdCamel,
          ),
        unit_id:
          normalizeId(
            unitId ??
              unitIdCamel,
          ),
        area_id:
          normalizeId(
            areaId ??
              areaIdCamel,
          ),
        subarea_id:
          normalizeId(
            subareaId ??
              subareaIdCamel,
          ),
      };

      const companyIds =
        scope.company_ids;
      const clientIds =
        scope.client_ids;
      const unitIds =
        scope.unit_ids;
      const areaIds =
        scope.area_ids;
      const subareaIds =
        scope.subarea_ids;

      const hasAnyScope =
        companyIds.length > 0 ||
        clientIds.length > 0 ||
        unitIds.length > 0 ||
        areaIds.length > 0 ||
        subareaIds.length > 0;

      if (!hasAnyScope) {
        return false;
      }

      /*
       * Um escopo pai restrito exige o ID do pai quando a consulta desce
       * para um filho.
       */
      if (
        companyIds.length &&
        (
          requested.client_id ||
          requested.unit_id ||
          requested.area_id ||
          requested.subarea_id
        ) &&
        !requested.company_id
      ) {
        return false;
      }

      if (
        clientIds.length &&
        (
          requested.unit_id ||
          requested.area_id ||
          requested.subarea_id
        ) &&
        !requested.client_id
      ) {
        return false;
      }

      if (
        unitIds.length &&
        (
          requested.area_id ||
          requested.subarea_id
        ) &&
        !requested.unit_id
      ) {
        return false;
      }

      if (
        areaIds.length &&
        requested.subarea_id &&
        !requested.area_id
      ) {
        return false;
      }

      const matches =
        (
          allowed,
          requestedId,
        ) => {
          if (!allowed.length) {
            return true;
          }

          return Boolean(
            requestedId &&
              allowed.includes(
                requestedId,
              ),
          );
        };

      return (
        matches(
          companyIds,
          requested.company_id,
        ) &&
        matches(
          clientIds,
          requested.client_id,
        ) &&
        matches(
          unitIds,
          requested.unit_id,
        ) &&
        matches(
          areaIds,
          requested.area_id,
        ) &&
        matches(
          subareaIds,
          requested.subarea_id,
        )
      );
    },
    [
      isGlobal,
      scope,
    ],
  );

  const inAnyScope =
    useCallback(
      (
        requests = [],
      ) =>
        arrayOrEmpty(
          requests,
        ).some((request) =>
          inScope(request),
        ),
      [inScope],
    );

  const inAllScopes =
    useCallback(
      (
        requests = [],
      ) =>
        arrayOrEmpty(
          requests,
        ).every((request) =>
          inScope(request),
        ),
      [inScope],
    );

  /*
   * =========================================================================
   * CONTEXTO / DIAGNÓSTICO
   * =========================================================================
   */

  const canViewModule =
    useCallback(
      (module) =>
        hasModule(module),
      [hasModule],
    );

  const moduleActions =
    useCallback(
      (module) =>
        objectOrEmpty(
          permissionMatrix?.[
            module
          ] ??
            modulePermissions?.[
              module
            ] ??
            matrix?.[module],
        ),
      [
        matrix,
        modulePermissions,
        permissionMatrix,
      ],
    );

  const moduleActionIds =
    useCallback(
      (module) =>
        Object.entries(
          moduleActions(module),
        )
          .filter(
            ([, enabled]) =>
              boolValue(enabled),
          )
          .map(([action]) =>
            action,
          ),
      [moduleActions],
    );

  const effectiveModule =
    useCallback(
      (module) => ({
        module,
        enabled:
          hasModule(module),
        actions:
          moduleActions(module),
        actionIds:
          moduleActionIds(module),
        feature:
          readNested(
            authorization,
            [
              "module_features",
              module,
            ],
          ) ??
          readNested(
            authorization,
            [
              "module_feature",
              module,
            ],
          ) ??
          null,
      }),
      [
        authorization,
        hasModule,
        moduleActionIds,
        moduleActions,
      ],
    );

  const getScopeIds =
    useCallback(
      (key) =>
        normalizeIds(
          scope?.[key],
        ),
      [scope],
    );

  const hasScope =
    useCallback(
      (key) =>
        getScopeIds(key).length >
        0,
      [getScopeIds],
    );

  const getRolePolicyValue =
    useCallback(
      (key, fallback = null) =>
        Object.prototype.hasOwnProperty.call(
          rolePolicy,
          key,
        )
          ? rolePolicy[key]
          : fallback,
      [rolePolicy],
    );

  const getConfigValue =
    useCallback(
      (key, fallback = null) => {
        const value =
          readNested(
            authorization,
            [
              "config",
              key,
            ],
          );

        return value ===
          undefined
          ? fallback
          : value;
      },
      [authorization],
    );

  /*
   * =========================================================================
   * SINCRONIZAÇÃO
   * =========================================================================
   */

  const authorizationDiagnostic =
    useCallback(
      () => ({
        authenticated: isAuthenticated,
        userId:
          authorization?.user_id ??
          user?.id ??
          null,
        role:
          authorization?.role ??
          user?.role ??
          null,
        modules,
        permissions: matrix,
        scope,
        configAccess,
        version:
          authorization?.authorization_context_version ??
          authorization?.authorization_version ??
          authorization?.matrix_version ??
          null,
        generatedAt:
          authorization?.authorization_generated_at ??
          authorization?.generated_at ??
          null,
      }),
      [
        authorization,
        configAccess,
        isAuthenticated,
        matrix,
        modules,
        scope,
        user?.id,
      ],
    );

  const refresh =
    useCallback(
      (silent = true) =>
        typeof refreshAuthorization ===
          "function"
          ? refreshAuthorization(
              silent,
            )
          : Promise.resolve(
              authorization,
            ),
      [
        authorization,
        refreshAuthorization,
      ],
    );

  const refreshPermissions =
    useCallback(
      (silent = true) =>
        refresh(silent),
      [refresh],
    );

  /*
   * =========================================================================
   * ESTADO
   * =========================================================================
   */

  const permissionState =
    useMemo(
      () => {
        let state =
          "ready";

        if (
          !isAuthenticated
        ) {
          state =
            "unauthenticated";
        } else if (
          authorizationLoading
        ) {
          state =
            "loading";
        } else if (
          authorizationError
        ) {
          state =
            "error";
        }

        return state;
      },
      [
        authorizationError,
        authorizationLoading,
        isAuthenticated,
      ],
    );

  const hasAuthorization =
    Boolean(
      authorization,
    );

  const authorizationVersion =
    authorization?.authorization_context_version ??
    authorization?.authorization_version ??
    authorization?.matrix_version ??
    authorization?.metadata?.matrix_version ??
    null;

  const authorizationGeneratedAt =
    authorization?.authorization_generated_at ??
    authorization?.generated_at ??
    authorization?.metadata?.generated_at ??
    null;

  /*
   * =========================================================================
   * COMPATIBILIDADE
   * =========================================================================
   *
   * Alguns módulos antigos consomem `permissions`, `modules`, `features` e
   * `config_access` diretamente do retorno do hook. Mantemos esses campos
   * explícitos para não quebrar consumidores.
   */

  return useMemo(
    () => ({
      // Identidade.
      user,
      role,
      roleRaw:
        authorization?.role ??
        user?.role ??
        null,
      isAuthenticated,
      isAdmin,
      isGlobal,

      // Estado da sincronização.
      authorization,
      authorizationLoading,
      authorizationError,
      permissionState,
      hasAuthorization,
      refresh,
      refreshPermissions,

      // Permissões.
      can,
      canAny,
      canAll,
      authorizationDiagnostic,
      matrix,
      permissions:
        matrix,
      permissionMatrix,

      // Módulos.
      modules,
      modulePermissions,
      hasModule,
      canModuleView,
      canViewModule,
      hasAnyModule,
      hasAllModules,
      moduleActions,
      moduleActionIds,
      effectiveModule,

      // Features.
      features,
      hasFeature,
      hasAnyFeature,
      hasAllFeatures,

      // Configuração.
      configAccess,
      config_groups:
        configAccess,
      hasConfig,
      canConfig,
      canAnyConfig,
      getConfigValue,

      // Escopo.
      scope,
      scopeMatrix,
      inScope,
      inAnyScope,
      inAllScopes,
      getScopeIds,
      hasScope,

      // Governança/delegação.
      rolePolicy,
      getRolePolicyValue,
      delegation,

      // ================================================================
      // TRÊS CONCEITOS FUNDAMENTAIS (separação explícita)
      // ================================================================

      // PRIMEIRO CONCEITO: O que o cliente possui (ClientModuleEntitlement)
      clientModules:
        authorization?.clientModules ||
        authorization?.client_modules ||
        EMPTY_OBJECT,

      // SEGUNDO CONCEITO: Quais dados o usuário pode enxergar (UserClientMembership)
      userClients:
        authorization?.userClients ||
        authorization?.user_clients ||
        EMPTY_OBJECT,

      // TERCEIRO CONCEITO: O que o usuário pode fazer (UserPermissionGrant)
      userPermissions:
        authorization?.userPermissions ||
        authorization?.user_permissions ||
        EMPTY_OBJECT,

      // Versão/timestamp.
      authorizationVersion,
      authorizationGeneratedAt,
      authorizationMetadata:
        authorization?.metadata ||
        EMPTY_OBJECT,
    }),
    [
      authorization,
      authorizationDiagnostic,
      authorizationError,
      authorizationLoading,
      can,
      canAll,
      canAny,
      canConfig,
      canModuleView,
      canViewModule,
      configAccess,
      delegation,
      effectiveModule,
      features,
      getConfigValue,
      getRolePolicyValue,
      getScopeIds,
      hasAllFeatures,
      hasAllModules,
      hasAnyFeature,
      hasAnyModule,
      hasAuthorization,
      hasConfig,
      hasFeature,
      hasModule,
      hasScope,
      inAllScopes,
      inAnyScope,
      inScope,
      isAdmin,
      isAuthenticated,
      isGlobal,
      matrix,
      moduleActionIds,
      moduleActions,
      modulePermissions,
      modules,
      permissionMatrix,
      permissionState,
      refresh,
      refreshPermissions,
      role,
      scope,
      scopeMatrix,
      user,
    ],
  );
}

export default usePermissions;
